/**
 * Pipeline Analytics Page - conversion funnel, pass reasons, investor reliability,
 * and feedback weights with adjustment history.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  BarChart3,
  TrendingDown,
  Award,
  Sliders,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealPipelineService, {
  ConversionAnalyticsFilters,
} from '../services/dealPipelineService';
import {
  ConversionFunnelData,
  FeedbackWeights,
  InvestorReliability,
  PassReasonBreakdown,
} from '../types/dealPipeline';
import { STAGE_CONFIG } from '../components/PipelineStageBadge';

const formatPct = (n?: number | null): string => {
  if (n == null) return '—';
  // Accept either fractions (0..1) or percentages (0..100)
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
};

const PipelineAnalyticsPage: React.FC = () => {
  const [conversion, setConversion] = useState<ConversionFunnelData | null>(null);
  const [loadingConversion, setLoadingConversion] = useState(true);
  const [reliability, setReliability] = useState<InvestorReliability[]>([]);
  const [loadingReliability, setLoadingReliability] = useState(true);
  const [weights, setWeights] = useState<FeedbackWeights | null>(null);
  const [loadingWeights, setLoadingWeights] = useState(true);

  const [filters, setFilters] = useState<ConversionAnalyticsFilters>({});

  const load = async () => {
    setLoadingConversion(true);
    setLoadingReliability(true);
    setLoadingWeights(true);

    const [convRes, relRes, wRes] = await Promise.all([
      dealPipelineService.getConversionAnalytics(filters),
      dealPipelineService.getInvestorReliability(),
      dealPipelineService.getFeedbackWeights(),
    ]);

    if (convRes.error) toast.error(convRes.error);
    else setConversion(convRes.data || null);
    setLoadingConversion(false);

    if (relRes.error) toast.error(relRes.error);
    else setReliability(relRes.data?.investors || []);
    setLoadingReliability(false);

    if (wRes.error) toast.error(wRes.error);
    else setWeights(wRes.data || null);
    setLoadingWeights(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const passReasons: PassReasonBreakdown[] = useMemo(() => {
    return conversion?.pass_reasons || [];
  }, [conversion]);

  const maxStageCount = useMemo(() => {
    if (!conversion?.stages?.length) return 0;
    return Math.max(...conversion.stages.map((s) => s.count));
  }, [conversion]);

  const maxPassReasonCount = useMemo(() => {
    if (!passReasons.length) return 0;
    return Math.max(...passReasons.map((p) => p.count));
  }, [passReasons]);

  const maxWeight = useMemo(() => {
    if (!weights?.dimensions?.length) return 1;
    return Math.max(...weights.dimensions.map((d) => Math.abs(d.current_weight)));
  }, [weights]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link
              to="/deal-pipeline"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to pipeline
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Pipeline Analytics</h1>
            <p className="text-sm text-gray-600 mt-1">
              Conversion funnel, pass reasons, investor reliability, and feedback weights.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Sector filter"
              value={filters.sector || ''}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, sector: e.target.value || undefined }))
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="number"
              placeholder="Investor ID"
              value={filters.investor_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  investor_id: v === '' ? undefined : parseInt(v, 10),
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_from: e.target.value || undefined }))
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="date"
              value={filters.date_to || ''}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_to: e.target.value || undefined }))
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Conversion funnel */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Conversion funnel
          </h2>

          {loadingConversion ? (
            <div className="flex items-center text-gray-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : !conversion?.stages?.length ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No conversion data yet
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <SummaryCard label="Total" value={String(conversion.total_pipelines ?? 0)} />
                <SummaryCard label="Funded" value={String(conversion.funded_count ?? 0)} color="text-green-600" />
                <SummaryCard label="Passed" value={String(conversion.passed_count ?? 0)} color="text-red-600" />
                <SummaryCard
                  label="Conversion rate"
                  value={formatPct(conversion.overall_conversion_rate)}
                  color="text-blue-600"
                />
              </div>

              <div className="space-y-2">
                {conversion.stages.map((s) => {
                  const cfg = STAGE_CONFIG[s.stage];
                  const widthPct =
                    maxStageCount > 0 ? (s.count / maxStageCount) * 100 : 0;
                  return (
                    <div key={s.stage} className="flex items-center gap-3">
                      <div className="w-36 flex-shrink-0 text-xs font-medium text-gray-700">
                        {cfg?.label || s.stage}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded h-7 relative overflow-hidden">
                        <div
                          className={`h-full ${cfg?.dot || 'bg-blue-500'} transition-all`}
                          style={{ width: `${widthPct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-700">
                          {s.count}
                        </div>
                      </div>
                      <div className="w-24 text-right text-xs text-gray-500">
                        {s.conversion_rate_from_previous != null
                          ? `${formatPct(s.conversion_rate_from_previous)} ↓`
                          : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* Pass reasons */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-red-600" />
            Pass reasons
          </h2>
          {loadingConversion ? (
            <div className="flex items-center text-gray-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : passReasons.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No pass reasons yet</div>
          ) : (
            <div className="space-y-2">
              {passReasons.map((r) => {
                const widthPct = maxPassReasonCount > 0 ? (r.count / maxPassReasonCount) * 100 : 0;
                return (
                  <div key={String(r.reason)} className="flex items-center gap-3">
                    <div className="w-44 flex-shrink-0 text-xs text-gray-700 capitalize">
                      {String(r.reason).replace(/_/g, ' ')}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded h-6 relative overflow-hidden">
                      <div
                        className="h-full bg-red-400 transition-all"
                        style={{ width: `${widthPct}%` }}
                      />
                      <div className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-700">
                        {r.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Investor reliability */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-4 h-4 text-purple-600" />
            Investor reliability
          </h2>
          {loadingReliability ? (
            <div className="flex items-center text-gray-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : reliability.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No reliability data yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 text-xs uppercase text-gray-500">
                    <th className="py-2 pr-3">Investor</th>
                    <th className="py-2 px-3">Soft commits</th>
                    <th className="py-2 px-3">Closed</th>
                    <th className="py-2 px-3">Soft → Close</th>
                    <th className="py-2 px-3">Avg days to close</th>
                    <th className="py-2 px-3">Total deals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...reliability]
                    .sort((a, b) => b.soft_to_close_ratio - a.soft_to_close_ratio)
                    .map((r) => (
                      <tr key={r.investor_id}>
                        <td className="py-2 pr-3">
                          <Link
                            to={`/investors/${r.investor_id}`}
                            className="text-gray-900 hover:text-blue-600"
                          >
                            {r.investor_name || `Investor #${r.investor_id}`}
                          </Link>
                          {r.firm_name && (
                            <div className="text-xs text-gray-500">{r.firm_name}</div>
                          )}
                        </td>
                        <td className="py-2 px-3">{r.soft_commitments}</td>
                        <td className="py-2 px-3">{r.closed}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              r.soft_to_close_ratio >= 0.5
                                ? 'bg-green-100 text-green-700'
                                : r.soft_to_close_ratio >= 0.25
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {formatPct(r.soft_to_close_ratio)}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-600">
                          {r.avg_days_to_close != null ? `${r.avg_days_to_close.toFixed(0)}d` : '—'}
                        </td>
                        <td className="py-2 px-3 text-gray-600">{r.total_deals ?? '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Feedback weights */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Sliders className="w-4 h-4 text-blue-600" />
            Feedback weights
          </h2>
          {loadingWeights ? (
            <div className="flex items-center text-gray-500 py-8 justify-center">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : !weights ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No weight data yet
            </div>
          ) : (
            <>
              {weights.last_updated && (
                <p className="text-xs text-gray-500 mb-3">
                  Last updated: {new Date(weights.last_updated).toLocaleString()}
                </p>
              )}

              <div className="space-y-2 mb-6">
                {(weights.dimensions || []).map((d) => {
                  const widthPct =
                    maxWeight > 0 ? (Math.abs(d.current_weight) / maxWeight) * 100 : 0;
                  const baseline = d.baseline_weight ?? 0;
                  const drift = d.current_weight - baseline;
                  return (
                    <div key={d.dimension} className="flex items-center gap-3">
                      <div className="w-36 flex-shrink-0 text-xs font-medium text-gray-700 capitalize">
                        {d.dimension.replace(/_/g, ' ')}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded h-6 relative overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${widthPct}%` }}
                        />
                        <div className="absolute inset-0 flex items-center px-2 text-xs font-medium text-gray-700">
                          {d.current_weight.toFixed(2)}
                        </div>
                      </div>
                      <div
                        className={`w-20 text-right text-xs ${
                          drift > 0
                            ? 'text-green-600'
                            : drift < 0
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}
                      >
                        {drift > 0 ? '+' : ''}
                        {drift.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {weights.adjustment_history && weights.adjustment_history.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                    Adjustment history
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs uppercase text-gray-500">
                          <th className="py-2 px-3">When</th>
                          <th className="py-2 px-3">Dimension</th>
                          <th className="py-2 px-3">Delta</th>
                          <th className="py-2 px-3">Trigger</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {weights.adjustment_history.map((adj, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-3 text-gray-600 text-xs">
                              {new Date(adj.timestamp).toLocaleString()}
                            </td>
                            <td className="py-2 px-3 capitalize">
                              {adj.dimension.replace(/_/g, ' ')}
                            </td>
                            <td
                              className={`py-2 px-3 font-medium ${
                                adj.delta > 0
                                  ? 'text-green-600'
                                  : adj.delta < 0
                                  ? 'text-red-600'
                                  : 'text-gray-600'
                              }`}
                            >
                              {adj.delta > 0 ? '+' : ''}
                              {adj.delta.toFixed(3)}
                            </td>
                            <td className="py-2 px-3 text-gray-600 text-xs">
                              {adj.trigger || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string;
  color?: string;
}> = ({ label, value, color = 'text-gray-900' }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
  </div>
);

export default PipelineAnalyticsPage;
